import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_ORG_SMS_MESSAGES } from '../../../lib/graphql/queries/organizations';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

export interface OrgSmsMessage {
  messageId: string;
  contactId: string | null;
  contactName: string | null;
  direction: string | null;
  body: string | null;
  status: string | null;
  sentAt: string | null;
  createdAt: string | null;
}

type Data = {
  organization?: {
    messages?: { items?: OrgSmsMessage[]; total?: number; hasMore?: boolean };
  } | null;
};

const PAGE_SIZE = 25;

/**
 * Paginated org-wide SMS list. Used both as the dashboard SMS drill-down
 * source and (when `conversationId` is provided) the thread-drill-down
 * inside a single SMS conversation.
 */
export const useOrgSmsMessagesGQL = ({
  dateRange,
  conversationId,
  enabled = true,
  sortOrder = 'DESC',
}: {
  dateRange: DateRange;
  conversationId?: string;
  enabled?: boolean;
  /**
   * `DESC` = newest first (table/inbox view, default).
   * `ASC` = oldest first for the thread chat view. We keep the sort on
   * the backend (it indexes `created_at`) instead of re-sorting in the
   * FE — inbound rows can carry `sent_at = NULL` which would otherwise
   * cluster them at the top of a client-side sort by `sentAt`.
   */
  sortOrder?: 'ASC' | 'DESC';
}) => {
  const orgId = useOrganizationStore((s) => s.organization?.id) ?? '';
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const { data, loading } = useQuery<Data>(GET_ORG_SMS_MESSAGES, {
    variables: {
      organizationId: orgId,
      limit: currentPageSize,
      offset: currentPage * currentPageSize,
      dateFrom: dateRange.from?.toISOString(),
      dateTo: dateRange.to?.toISOString(),
      search: search || undefined,
      conversationId,
      sortOrder,
    },
    skip: !enabled || !orgId,
    fetchPolicy: 'cache-and-network',
  });

  const items = data?.organization?.messages?.items ?? [];
  const total = data?.organization?.messages?.total ?? 0;
  const hasMore = data?.organization?.messages?.hasMore ?? false;

  const nextPage = useCallback(() => setCurrentPage((p) => p + 1), []);
  const prevPage = useCallback(() => setCurrentPage((p) => Math.max(0, p - 1)), []);
  const goToPage = useCallback((page: number) => setCurrentPage(Math.max(0, page)), []);
  const changePageSize = useCallback((size: number) => {
    setCurrentPageSize(size);
    setCurrentPage(0);
  }, []);
  const setSearchTerm = useCallback((value: string | undefined) => {
    setSearch(value);
    setCurrentPage(0);
  }, []);

  return {
    items,
    total,
    hasMore,
    isLoading: loading,
    currentPage,
    pageSize: currentPageSize,
    nextPage,
    prevPage,
    goToPage,
    changePageSize,
    setSearch: setSearchTerm,
    search,
  };
};
